import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { Button } from '@praktio/common-ui';
import { Helmet } from 'react-helmet';
import { Constants } from '@praktio/common';
import classNames from 'classnames';

/* Ducks */
import { RootState } from '../../ducks/store';
import {
  getLearnerModule,
  markLearnerViewed,
  setLearnerCompleted,
  getLearnerModuleNext,
  updateShowReviewRetake,
  getLearnerCourse,
  updateSlideData,
  updateLearnerStatus,
  clearLearnerCourse,
  updateLearnerLoading,
  updateSlideId,
} from '../../ducks/learner';
import { showToC as setShowToC } from '../../ducks/toc';
import { showModal } from '../../ducks/modal';
import { MessageTypes, ModalTypes, ModuleTypes, RetakeBadgeTooltip } from '../../utils/constants';
import {
  notifyError,
  processModuleAvailability,
  setAuthorShowToC,
  setAuthorTitle,
} from '../../utils/functions';

/* Components */
import CourseInfo from './CourseInfo';
import ToC from './ToC/ToC';
import { Module } from './Module';
import OverlaySpinner from '../common/OverlaySpinner/OverlaySpinner';

import './CourseViewer.scss';

const { TimeEventTypes } = Constants;
const iframeOrigin = process.env.REACT_APP_IFRAME_ORIGIN || window.origin;

// Polling duration of 30 seconds: 30 * 1 second
const PollingDuration = 30 * 1000;

// Inactive duration of Ten minutes: 10 * 60 * 1 second
const InactiveDuration = 10 * 60 * 1000;

const CourseViewer = () => {
  const navigate = useNavigate();
  const params = useParams();
  const courseId = params.courseId as string;
  const moduleId = params.moduleId as string;
  const slideId = params.slideId as string;
  const dispatch = useDispatch();
  // The tocRef and contentRef are needed by CSSTransition to prevent warnings
  const tocRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const lastActive = useRef(new Date());
  const statusInterval = useRef<NodeJS.Timeout>();
  const hasMounted = useRef(false);
  const prevModuleId = useRef<string>();

  const [showNext, setShowNext] = useState(false);
  const [disableNext, setDisableNext] = useState(false);
  const [courseLoaded, setCourseLoaded] = useState(false);
  const [showModuleCompleteNotice, setShowModuleCompleteNotice] = useState(false);
  const {
    module,
    loading,
    showToC,
    isLast,
    showReviewRetake,
    course,
    currentModuleCompleteStatus,
    isRetake,
  } = useSelector((state: RootState) => {
    const { learner } = state;
    const { course, loading, showReviewRetake } = learner;
    const module = course?.modules?.find((m) => m._id === moduleId);
    const isRetake = module?.isRetake;
    const showToC = state.toc.show;
    const currentModuleCompleteStatus = module?.completed;
    let isLast = true;
    if (course) {
      const moduleAvailability = processModuleAvailability(course);
      let beforeCurrent = true;
      moduleAvailability.forEach((m) => {
        if (m.module._id === moduleId) {
          beforeCurrent = false;
        }
        // There are other modules available to move next to only if
        // the module is available and we aren't before the current module
        // or we are before the current module and the prior isn't completed
        else if (m.isAvailable && (!beforeCurrent || !m.module.completed)) {
          isLast = false;
        }
      });
    }

    return {
      course,
      loading,
      module,
      showToC,
      isLast,
      showReviewRetake,
      currentModuleCompleteStatus,
      isRetake,
    };
  });

  const [showRetakeBadge, setShowRetakeBadge] = useState(isRetake);
  const [moduleLoaded, setModuleLoaded] = useState(false);
  const isRetakeRef = useRef(isRetake);
  const currentModuleCompleteStatusRef = useRef(currentModuleCompleteStatus);
  const currentModuleRef = useRef(module);

  const modIsDocOrEmbed = [ModuleTypes.Document, ModuleTypes.Embedded].includes(module?.type);

  useEffect(() => {
    if (course) {
      setCourseLoaded(true);
    } else {
      setCourseLoaded(false);
    }
  }, [course]);

  useEffect(() => {
    setShowModuleCompleteNotice(false);
    if (module) {
      if (
        currentModuleRef.current?._id === module._id &&
        !currentModuleCompleteStatusRef.current &&
        currentModuleCompleteStatus &&
        !modIsDocOrEmbed
      ) {
        setShowModuleCompleteNotice(true);
      }
      currentModuleCompleteStatusRef.current = currentModuleCompleteStatus;
      currentModuleRef.current = module;
    }
  }, [currentModuleCompleteStatus, module, modIsDocOrEmbed]);

  useEffect(() => {
    const messageListener = (e: MessageEvent<any>) => {
      if (e.origin === iframeOrigin) {
        if (e.data) {
          const messageData = e.data as IMessageData;
          const { type, data } = messageData || {};
          switch (type) {
            case MessageTypes.Completed:
              dispatch(setLearnerCompleted({ courseId, moduleId }));
              break;
            case MessageTypes.NextModule:
              const nextData: {
                show: boolean;
                advanceNext?: boolean;
                unlocked?: boolean;
                completed?: boolean;
              } = data;
              setShowNext(nextData.show);
              setDisableNext(!nextData.completed);
              if (nextData.advanceNext && !isLast) {
                dispatch(getLearnerModuleNext({ courseId, moduleId, navigate }));
              }
              break;
            case MessageTypes.Slide:
              navigate(
                `/courses/${courseId}/modules/${moduleId}${
                  module?.slides?.length && data._id ? `/slides/${data._id}` : ''
                }`,
              );
              dispatch(updateSlideData({ courseId, moduleId, slideData: data }));
              break;
            case MessageTypes.SlideLoading:
              dispatch(updateLearnerLoading(true));
              break;
            case MessageTypes.SlideLoaded:
              setModuleLoaded(true);
              dispatch(updateLearnerLoading(false));
              dispatch(updateSlideId({ courseId, slideId: data?._id }));
              break;
            case MessageTypes.Loaded:
              setAuthorTitle(iframeRef, module?.title);
              setAuthorShowToC(iframeRef, showToC);
              break;
            case MessageTypes.Error:
              notifyError({ message: data }, dispatch);
              break;
            case MessageTypes.ContactUs:
              dispatch(showModal(ModalTypes.Feedback));
              break;
            default:
              break;
          }
        }
      }
    };

    window.addEventListener('message', messageListener);
    return () => {
      window.removeEventListener('message', messageListener);
    };
  }, [courseId, moduleId, showToC, isLast, navigate, dispatch]);

  useEffect(() => {
    // When the moduleId remains the same, only show the retake
    // badge if the isRetake status has changed from false/undefined to true
    if (moduleId === prevModuleId.current) {
      if (isRetake !== isRetakeRef.current) {
        if (isRetake) {
          setShowRetakeBadge(true);
        }
      }
      // When the moduleId changes, show the retake badge if the
      // new module's isRetake is true. Otherwise, hide the retake badge
    } else {
      setModuleLoaded(false);
      if (isRetake) {
        setShowRetakeBadge(true);
      } else {
        setShowRetakeBadge(false);
      }
    }
    isRetakeRef.current = isRetake;
  }, [isRetake, moduleId]);

  // useEffect for polling status back to API
  useEffect(() => {
    const activeStatus = () => {
      if (moduleId) {
        const now = new Date();
        const duration = +now - +lastActive.current;
        if (duration <= InactiveDuration) {
          dispatch(
            updateLearnerStatus({
              courseId,
              moduleId,
              lastActive: lastActive.current,
            }),
          );
        }
      }
    };

    // Initial mount/initialization
    if (!hasMounted.current) {
      hasMounted.current = true;
      if (moduleId) {
        statusInterval.current = setInterval(activeStatus, PollingDuration);
      }
    }
    // Otherwise, run when moduleId is defined
    else if (moduleId) {
      if (statusInterval.current || statusInterval.current === 0) {
        clearInterval(statusInterval.current);
      }
      statusInterval.current = setInterval(activeStatus, PollingDuration);
    }
    // If moduleId is not defined, clear the interval to prevent additional updates
    else if (statusInterval.current || statusInterval.current === 0) {
      clearInterval(statusInterval.current);
    }

    prevModuleId.current = moduleId;

    return () => {
      if (moduleId && (statusInterval.current || statusInterval.current === 0)) {
        clearInterval(statusInterval.current);
      }
    };
  }, [courseId, moduleId, dispatch]);

  useEffect(() => {
    if (courseId) {
      if (moduleId) {
        dispatch(getLearnerModule({ courseId, moduleId, navigate }));
      } else {
        dispatch(getLearnerCourse({ courseId }));
        dispatch(setShowToC(true));
      }
    }
    setShowNext(false);
  }, [courseId, moduleId, dispatch]);

  useEffect(() => {
    const { authorId, video, document, embedded, completed, isUnlocked } = module || {};
    if (!authorId && (video || document || embedded)) {
      setShowNext(true);
      setDisableNext(isUnlocked ? false : !completed);
      setModuleLoaded(true);
    }
  }, [module]);

  useEffect(() => {
    return () => {
      // Can't use moduleId here because:
      // - we want this useEffect to run once, on unmount
      // - moduleId won't get updated unless it's in the depedency list
      // and adding it to that list will make this run more than once
      // so we set moduleId using prevModuleId ref which is set in
      // a separate useEffect
      if (courseId && prevModuleId.current) {
        // If a courseId and moduleId exist, update status
        // to send a Stop event since user is moving away from CourseViewer
        dispatch(
          updateLearnerStatus({
            courseId,
            moduleId: prevModuleId.current,
            lastActive: new Date(),
            eventType: TimeEventTypes.Stop,
          }),
        );
      }
      // When CourseViewer unmounts, clear the learner's course to
      // properly update for TopNav
      dispatch(clearLearnerCourse());
    };
    // Disabling warning here because we're using this useEffect
    // as an unmount, so it should only run when leaving the screen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recordActivity = useCallback(() => {
    if (courseId && moduleId) {
      const now = new Date();
      const duration = +now - +(lastActive.current || now);
      lastActive.current = now;
      if (duration > InactiveDuration) {
        dispatch(
          updateLearnerStatus({
            courseId,
            moduleId,
            lastActive: lastActive.current,
          }),
        );
      }
    }
  }, [courseId, moduleId, dispatch]);

  return (
    <div
      className={classNames('CourseViewer', module?.type)}
      onMouseOver={recordActivity}
      onMouseMove={recordActivity}
      onKeyDown={recordActivity}
      onFocus={recordActivity}
    >
      <Helmet>
        <title>{course?.title || 'Course'} - Praktio</title>
      </Helmet>
      {!courseLoaded && <OverlaySpinner />}
      {course && (
        <>
          <CSSTransition
            in={showToC && courseLoaded}
            timeout={300}
            classNames="ToC-animation"
            nodeRef={tocRef}
          >
            <ToC
              ref={tocRef}
              iframeRef={iframeRef}
              currentSlideId={slideId}
              course={course}
              showToC={showToC}
              showReviewRetake={!!showReviewRetake && !module?.disableRetake}
              loaded={moduleLoaded}
            />
          </CSSTransition>
          <CSSTransition
            in={showToC && courseLoaded}
            timeout={300}
            classNames="CourseViewer-animation"
            nodeRef={contentRef}
          >
            <div
              className={classNames('CourseViewer-content', {
                'toc-open': showToC,
                'toc-closed': !showToC,
              })}
              ref={contentRef}
            >
              <>
                {!moduleId && <CourseInfo course={course} />}
                {moduleId && (
                  <>
                    {moduleId && !showReviewRetake && showRetakeBadge && (
                      <div className="CourseViewer-retake-badge-container">
                        <div className="CourseViewer-retake-badge">
                          <div className="CourseViewer-retake-badge-text">Retake Mode</div>
                          <OverlayTrigger
                            placement="bottom"
                            overlay={
                              <Popover id="retake-badge-tooltip">
                                <Popover.Content>{RetakeBadgeTooltip}</Popover.Content>
                              </Popover>
                            }
                          >
                            <span className="material-icons-outlined">info</span>
                          </OverlayTrigger>
                        </div>
                      </div>
                    )}
                    {loading && <OverlaySpinner className="overlay-absolute" />}
                    <Module
                      courseId={courseId}
                      moduleId={moduleId}
                      iframeRef={iframeRef}
                      showReviewRetake={!!showReviewRetake}
                      showToC={showToC}
                      iframeOrigin={iframeOrigin}
                      setInModule={() => dispatch(updateShowReviewRetake(false))}
                      viewed={() => {
                        dispatch(markLearnerViewed({ courseId, moduleId }));
                        if (!isLast && localStorage.getItem('autoAdvance') === 'true') {
                          dispatch(getLearnerModuleNext({ courseId, moduleId, navigate }));
                        }
                      }}
                    />
                    {showNext && !loading && (
                      <div className="CourseViewer-next">
                        <Button
                          className="CourseViewer-button"
                          disabled={disableNext}
                          onClick={() => {
                            setShowModuleCompleteNotice(false);
                            if (courseId && moduleId) {
                              // When NEXT button is clicked, update module status
                              // to specify a Next (or Stop if isLast is true) event
                              dispatch(
                                updateLearnerStatus({
                                  courseId,
                                  moduleId,
                                  lastActive: new Date(),
                                  eventType: isLast ? TimeEventTypes.Stop : TimeEventTypes.Next,
                                }),
                              );
                            }

                            if (isLast) {
                              navigate('/');
                            } else {
                              dispatch(getLearnerModuleNext({ courseId, moduleId, navigate }));
                            }

                            setShowNext(false);
                          }}
                          btnStyle="primary"
                        >
                          {isLast ? (
                            <>
                              <span className="responsive-button-text">EXIT</span>
                              <span className="material-icons">exit_to_app</span>
                            </>
                          ) : (
                            <>
                              <span className="responsive-button-text">NEXT</span>
                              <span className="material-icons">arrow_forward</span>
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </>
            </div>
          </CSSTransition>
        </>
      )}
      <CSSTransition
        in={showModuleCompleteNotice}
        timeout={300}
        classNames="ModuleCompleteNotice-animation"
        mountOnEnter={true}
        unmountOnExit={true}
      >
        <div className="ModuleCompleteNotice d-flex align-items-center">
          <span className="material-icons">done</span>
          <div className="ModuleCompleteNotice-text">Module Complete!</div>
        </div>
      </CSSTransition>
    </div>
  );
};

export default CourseViewer;
